import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { getTabList, Icon } from '@umijs/max'
import type { InitialStateTypes } from '@/utils/types'
import type { RunTimeLayoutConfig } from '@umijs/max';
import logger from './ali-logger';

// 注册阿里ARMS到window对象
window.aliLogger = logger;


import defaultSettings from '../config/defaultSettings';
import { fetchConfig, queryCurrentUser } from './services/logic/login';
import umiRequest from './utils/umiRequest';
import { getLocalStorageItem, setLocalStorageItem, logoutToLogin, forceRedirect, initInitialState } from './utils';
import { LOCAL_STORAGE, ROUTES } from './utils/enums';
import { eq, get } from 'lodash-es';
import { BasicLayout } from '@/components/Business/BasicLayout';
import TabsLayout, { TabsLayoutProps } from '@/components/Business/TabsLayout';

import './global.less';
import { getDisputeReason } from './services/saas-dispute/allSessions';
import { fetchUserAPI } from './services/system';
import { getAccessToken } from './utils/common/authToken';
import { clearLocalStorageByModuleMapping } from './utils/utils';
import { getCountryLanguages } from './services/translate';

clearLocalStorageByModuleMapping()

/**
 *
 * @description umiUse-patchRoutes 路由配置修改
 * @export
 * @param {*} { routes }
 */
export function patchRoutes({ routes }: any) {
  for (let key in routes) {
    const icon = routes[key].icon;
    if (!icon) { continue; }
    if (icon?.includes('@/assets')) {
      const iconPath = icon.replace('@/assets', '')
      routes[key].icon = <img src={iconPath} width={16} height={16} />;
    } else {
      routes[key].icon = <Icon icon={icon} className="qd-text-base" style={{ fontSize: '1.2rem' }} />;
    }
  }
}

/**
 * @description 全局初始状态设置
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * @return initialState InitialStateTypes
 */
export async function getInitialState(): Promise<InitialStateTypes> {

  // 获取 LAYOUT 的值
  const LAYOUT_SETTINGS = defaultSettings;
  // 获取 ACCESS_TOKEN
  const ACCESS_TOKEN = getAccessToken() || undefined;


  // 存储到 localstorage
  setLocalStorageItem(LOCAL_STORAGE.LAYOUT, LAYOUT_SETTINGS)

  // 初始化数据
  const initialState: InitialStateTypes = {
    accessToken: ACCESS_TOKEN,
    settings: LAYOUT_SETTINGS as LayoutSettings,
    collapsed: true,
    fetchUserInfo: async () => await queryCurrentUser(),
    fetchDisputeReason: async () => await getDisputeReason(),
    forceCollapsed: true,
    languageMap: [],
    fetchUser: async () => await fetchUserAPI({}),
    fetchCountryLanguages: async () => await getCountryLanguages(),
    // fetchConfigInfo: async () => get(await fetchConfig(), 'data', []),
  }


  // 判断是否登录，没有登录跳转到登录页
  if (!ACCESS_TOKEN) {
    logoutToLogin();
    return initialState
  }

  // 判断在登录页是否已登录，已登录则跳转主页
  if (eq(location.pathname, ROUTES.LOGIN) && ACCESS_TOKEN) {
    forceRedirect(ROUTES.HOME);
  }

  // 如果不是登录页面，执行
  if (!eq(location.pathname, ROUTES.LOGIN)) {
    const result = await initInitialState(initialState)
    // NOTE 浮动聊天 SDK 灰度白名单：仅名单内用户 id 初始化 SDK
    const CHAT_SDK_USER_WHITELIST: Array<number | string> = [
      93,
      97,
      1,
      198,
      474,
      162
    ];
    if (CHAT_SDK_USER_WHITELIST.includes(result.currentUser?.id)) {
      // NOTE 接入浮动聊天 SDK（chat_svelte）：约定式配置 + 命令队列
      // SDK 脚本加载后自动读取 CHAT_SDK_CONFIG 完成初始化，并消化 $chat 积压命令；CSS 已内联进 JS
      const w = window as any
      // NOTE SDK 已内置默认后端地址（SSE 版，见 SSEService.DEFAULT_API_BASE_URL），无需传 url；本地联调可临时覆盖 url 字段
      // 必须设置非空对象才会触发约定式自动初始化
      w.CHAT_SDK_CONFIG = { env: 'dev' };
      const $chat: unknown[][] = (w.$chat = w.$chat || []);
      $chat.push(['set', 'user:user_id', [String(result.currentUser?.id ?? '')]]);
      $chat.push(['set', 'user:contact_name', [result.currentUser?.name ?? '']]);
      $chat.push(['set', 'user:web_url', [window.location.host ?? '']]);
      const chatScript = document.createElement('script');
      chatScript.src = 'https://qdesk-test-apps.oss-cn-shenzhen.aliyuncs.com/dist-cdn/chat-sdk.iife-dev-1.0.js';
      chatScript.async = true;
      document.body.appendChild(chatScript);
    }
    return result
  }
  return initialState
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
/**
 *
 *
 * @param {*} {
 *   initialState,
 * }
 * @return {*}
 */
export const layout: RunTimeLayoutConfig = BasicLayout;

/**
 * @description: 完全覆盖内置的多 Tabs 组件，需要搭配配置 hasCustomTabs:true 使用。
 * @doc https://alitajs.com/zh-CN/docs/guides/tabs#getcustomtabs
 */
export const getCustomTabs = () => (props: TabsLayoutProps) => <TabsLayout {...props} />

/**
 * @description: request 配置，可以配置错误处理，它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = umiRequest;

export function onRouteChange() {
    window.lfq('track', 'pageView');
}
